import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'

/**
 * The shared loading state (PX-W6.2) — pulsing skeleton rows in the verdant
 * token set, no spinner. `role="status"` so screen readers announce the wait.
 *
 * PX-W6.6 stamps `data-triad="loading"` on the root. The marker is what the
 * triad audit asserts, so the audit checks the SHARED component is mounted
 * rather than matching a page's copy — a page that hand-rolled its own spinner
 * fails the audit even when it looks right.
 */
export function LoadingPanel({
  label,
  rows = 3,
  className
}: {
  /** Overrides the default "Loading…" copy when the surface can be specific. */
  label?: string
  rows?: number
  className?: string
}) {
  const { t } = useTranslation('common')
  return (
    <div
      role="status"
      data-triad="loading"
      aria-label={label ?? t('states.loading')}
      className={cn('rounded-lg border border-border bg-card px-4 py-6', className)}
    >
      <div className="mx-auto flex max-w-sm flex-col items-center gap-1">
        <span className="text-sm text-muted-foreground">{label ?? t('states.loading')}</span>
        <div className="mt-2 w-full space-y-2" aria-hidden>
          {Array.from({ length: rows }, (_, index) => (
            <div
              key={index}
              className="h-3 animate-pulse rounded bg-muted"
              style={{ width: `${88 - index * 18}%` }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
